import { useState } from "react";
import { useSelector } from "react-redux";
import { Form, Row, Col, Button } from "react-bootstrap";
import { buildDashboardModule, buildDashboardPayload } from "./dashboardPayloadConstants.js";
import DialogFooter from "./DialogFooter.js";

const CreateAlertView = ({
  initialSettings,
  onHide,
  alertsCount,
  isUpdateMode = false,
  reloadDashboard,
  toastRef,
}) => {
  // Default options for 'From' and 'Alert Type'
  const defaultFromOptions = [
    { label: "My Projects", value: "My Projects", checked: false },
    { label: "All Projects", value: "All Projects", checked: false },
  ];
  // Redux user info
  const { userInformation } = useSelector((state) => state.UserReducer);

  const defaultAlertTypeOptions = [
    { label: "Copy XML Error", value: "Copy XML Error", checked: false },
    { label: "POA Creation Error", value: "POA Creation Error", checked: false },
    { label: "POA Update error", value: "POA Update error", checked: false },
    { label: "Change detected", value: "Change detected", checked: false },
  ];

  // Parse initial checked values from initialSettings
  const initialFrom =
    initialSettings?.From?.split(",")
      .map((f) => f.trim())
      .filter(Boolean) || [];
  const initialAlertType =
    initialSettings?.AlertType?.split(",")
      .map((f) => f.trim())
      .filter(Boolean) || [];

  const [fromOptions, setFromOptions] = useState(
    defaultFromOptions.map((opt) => ({
      ...opt,
      checked: initialFrom.includes(opt.value),
    })),
  );
  const [alertTypeOptions, setAlertTypeOptions] = useState(
    defaultAlertTypeOptions.map((opt) => ({
      ...opt,
      checked: initialAlertType.includes(opt.value),
    })),
  );

  const handleCheckboxChange = (optionType, index) => {
    if (optionType === "from") {
      const updated = [...fromOptions];
      updated[index].checked = !updated[index].checked;
      setFromOptions(updated);
    } else if (optionType === "alertType") {
      const updated = [...alertTypeOptions];
      updated[index].checked = !updated[index].checked;
      setAlertTypeOptions(updated);
    }
  };

  // Helper to build payload, similar to CreateTaskView
  const buildDashboardAlertPayload = () => {
    const getChecked = (options) =>
      options
        .filter((opt) => opt.checked)
        .map((opt) => opt.value.trim())
        .join(",");
    // Handle taskCount undefined or NaN
    let moduleName;
    if (isUpdateMode) {
      moduleName = initialSettings?.Title;
    } else {
      const count = typeof alertsCount === "number" && !isNaN(alertsCount) ? alertsCount : 0;
      moduleName = `Alerts_${count + 1}`;
    }
    // You can add userId or other info if needed, for now just use config
    const module = buildDashboardModule({
      moduleId: isUpdateMode && initialSettings?.Module_ID ? initialSettings.Module_ID : "",
      moduleName: moduleName,
      moduleType: "Alerts",
      userId: userInformation?.userid,
      config: {
        From: getChecked(fromOptions),
        AlertType: getChecked(alertTypeOptions),
      },
      Sequence: isUpdateMode ? initialSettings?.Sequence : -1
    });
    return buildDashboardPayload({ module });
  };

  const [submitting, setSubmitting] = useState(false);

  const handleCreateOrUpdate = async () => {
    setSubmitting(true);
    const payload = buildDashboardAlertPayload();
    try {
      const { createOrUpdateDashboardModuleView } = await import("../../../apis/dashboardApi.js");
      await createOrUpdateDashboardModuleView(payload);
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "success",
          summary: "Success",
          detail: isUpdateMode
            ? "Alert module updated successfully"
            : "Alert module created successfully",
          life: 3000,
        });
      }
      if (typeof reloadDashboard === "function") reloadDashboard();
      onHide();
    } catch (err) {
      if (toastRef && toastRef.current) {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to create/update alert module.",
          life: 5000,
        });
      }
      console.error("Error creating/updating alert module:", err);
      setSubmitting(false);
    }
  };

  const isFormValid =
    fromOptions.some((opt) => opt.checked) && alertTypeOptions.some((opt) => opt.checked);

  return (
    <>
      <div style={{ background: "#f6f8fa", borderRadius: 8, padding: 24 }}>
        <Row className="alert-form">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="mb-2 module-form-label">
                From <span className="mandatory-asterisk"> *</span>
              </Form.Label>
              <div className="d-flex flex-column gap-2 ms-3">
                {fromOptions.map((option, idx) => (
                  <Form.Check
                    key={option.value}
                    type="checkbox"
                    id={`from-${option.value}`}
                    label={option.label}
                    checked={option.checked}
                    onChange={() => handleCheckboxChange("from", idx)}
                  />
                ))}
              </div>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="mb-2 module-form-label">
                Alert Type <span className="mandatory-asterisk"> *</span>
              </Form.Label>
              <div className="d-flex flex-column gap-2 ms-3">
                {alertTypeOptions.map((option, idx) => (
                  <Form.Check
                    key={option.value}
                    type="checkbox"
                    id={`alertType-${option.value}`}
                    label={option.label}
                    checked={option.checked}
                    onChange={() => handleCheckboxChange("alertType", idx)}
                  />
                ))}
              </div>
            </Form.Group>
          </Col>
        </Row>
      </div>
      <DialogFooter
        submitting={submitting}
        isFormValid={isFormValid}
        isUpdateMode={isUpdateMode}
        onCancel={onHide}
        onSubmit={handleCreateOrUpdate}
      />
    </>
  );
};

export default CreateAlertView;
